/**
 * A folha de configuracao: as abas Configuracao e Sistema, por cima do painel.
 *
 * Responsabilidade: abrir, fechar, e dizer qual das duas abas esta na frente.
 *
 * NAO e responsabilidade deste arquivo: desenhar o conteudo das abas. Quem
 * monta os paineis de dentro e o `views/app.ts`, que os passa prontos — esta
 * folha nao sabe o que e um setpoint nem um comando, e por isso nao tem
 * `atualizar()`: cada painel de dentro ja assina o `store` por conta propria.
 *
 * ## Por que uma folha, e nao uma terceira pagina
 *
 * Mesma forma do painel local do `gfc` (`firmware/data/index.html`, a `.sheet`,
 * com estas duas abas e estes dois rotulos). Os dois aplicativos ficam lado a
 * lado na tela inicial do mesmo celular, olhando para o mesmo cultivo: quem
 * aprendeu que "configuracao mora atras do botao do canto" num deles nao devia
 * ter que reaprender no outro.
 *
 * O que fica de fora e o que fica dentro segue a mesma divisao do painel local:
 * a home e o que se OLHA (leituras, grafico, saidas, timelapse) e a folha e o
 * que se MEXE de vez em quando (setpoints) ou o que se consulta quando algo
 * deu errado (o feed de comandos, a conta). Ajustes e comandos na home
 * empurravam o timelapse para baixo da dobra em qualquer celular.
 *
 * ## `<dialog>` nativo, e nao backdrop + div fixa
 *
 * O `gfc` desenha a folha com dois elementos e um `hidden` em cada, porque o
 * painel local e HTML estatico servido pelo proprio controlador. Aqui e um
 * `<dialog>` com `showModal()`, que traz de graca o backdrop, o Esc, o foco
 * preso dentro da folha e o `inert` no resto da pagina. A aparencia e a mesma;
 * o que muda e nao ter que reimplementar nada disso a mao — e `.dialogo` ja
 * tinha estabelecido o `<dialog>` nativo nesta PWA.
 */

import { iconeUi } from "./../icons";
import { el } from "./../ui";

export interface Aba {
  /** Vira `data-aba`, e e o que `abrir()` recebe. */
  id: string;
  rotulo: string;
  conteudo: HTMLElement[];
}

export interface Folha {
  raiz: HTMLDialogElement;
  /** Sem `aba`, reabre na ultima que estava aberta. */
  abrir: (aba?: string) => void;
  fechar: () => void;
}

/**
 * Chamado quando uma aba passa a estar VISIVEL, seja qual for o caminho.
 *
 * Existe porque "a aba foi vista" e um fato do qual algo depende — a de
 * Alertas marca o feed como lido — e havia tres caminhos ate ela: o sino do
 * cabecalho, o botao de configuracao, e reabrir a folha na ultima aba. Pendurar
 * a acao no `onclick` de um deles fazia os outros dois nao marcarem nada, que
 * foi exatamente o que o gate pegou.
 */
type AoMostrar = (aba: string) => void;

export function montar(abas: Aba[], aoMostrar: AoMostrar = () => {}): Folha {
  const itens = abas.map((aba, indice) => {
    const primeira = indice === 0;
    const painel = el(
      "div",
      {
        class: "tab-panel",
        id: `aba-${aba.id}`,
        "data-aba": aba.id,
        role: "tabpanel",
        hidden: !primeira,
      },
      ...aba.conteudo,
    );
    const botao = el(
      "button",
      {
        type: "button",
        class: `tab ${primeira ? "active" : ""}`.trim(),
        "data-aba": aba.id,
        role: "tab",
        "aria-selected": String(primeira),
        "aria-controls": `aba-${aba.id}`,
        onclick: () => {
          mostrar(aba.id);
          aoMostrar(aba.id);
        },
      },
      aba.rotulo,
    );
    return { id: aba.id, botao, painel };
  });

  /** A aba que esta na frente agora — inclusive com a folha fechada. */
  let visivel = abas[0]?.id ?? "";

  function mostrar(id: string): void {
    for (const item of itens) {
      const ativa = item.id === id;
      item.painel.hidden = !ativa;
      item.botao.classList.toggle("active", ativa);
      item.botao.setAttribute("aria-selected", String(ativa));
    }
    visivel = id;
  }

  const raiz = el(
    "dialog",
    { class: "folha", id: "folha", "aria-label": "Configuração" },
    el(
      "div",
      { class: "folha-cabeca" },
      el("div", { class: "tabs", role: "tablist" }, ...itens.map((i) => i.botao)),
      el(
        "button",
        {
          type: "button",
          class: "icon-btn",
          "aria-label": "Fechar",
          onclick: () => raiz.close(),
        },
        iconeUi("fechar"),
      ),
    ),
    el("div", { class: "folha-corpo" }, ...itens.map((i) => i.painel)),
  );

  return {
    raiz,
    abrir: (aba?: string) => {
      if (aba !== undefined) mostrar(aba);
      // `showModal()` num dialogo ja aberto lanca — e reabrir tambem perderia a
      // posicao de scroll de quem so trocou de aba.
      if (!raiz.open) raiz.showModal();
      // Depois do `showModal()`: o aviso e "esta aba esta na tela AGORA", e
      // antes de abrir ela ainda nao estava.
      aoMostrar(visivel);
    },
    fechar: () => raiz.close(),
  };
}
